import { useState } from "react";
import { api, errorText, useFetch } from "../api";
import { Alerts, Badge, EmptyRow, Field, Loading, Modal, Page, Photo } from "../components/ui";
import { text, when } from "../format";

const EMPTY = { title: "", description: "", requiredDirects: "", status: "active" };

function formFrom(row) {
  if (!row) return EMPTY;
  return {
    title: row.title || "",
    description: row.description || "",
    requiredDirects: row.requiredDirects ?? "",
    status: row.status || "active",
  };
}

function checkImage(file) {
  if (!file) return "";
  if (file.type && !["image/jpeg", "image/png", "image/webp"].includes(file.type)) return "Use a jpg, png or webp image";
  if (file.size > 2 * 1024 * 1024) return "Image must be 2MB or smaller";
  return "";
}

export default function Rewards() {
  const list = useFetch("/rewards");
  const rows = Array.isArray(list.data?.data) ? list.data.data : [];
  const [editor, setEditor] = useState(null);
  const [notice, setNotice] = useState("");

  return (
    <Page
      title="Rewards"
      text="A member can claim a reward when their direct team reaches the required count. Inactive rewards stay hidden on the website."
      actions={<button type="button" className="btn" onClick={() => setEditor(EMPTY)}>New reward</button>}
    >
      <Alerts error={list.error} notice={notice} />
      {list.loading ? <Loading /> : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Image</th><th>Title</th><th>Directs</th><th>Status</th><th>Created</th>
              </tr>
            </thead>
            <tbody>
              {rows.length ? rows.map((row) => (
                <tr key={row.id} className="click" onClick={() => setEditor(row)}>
                  <td><Photo src={row.image} alt="" /></td>
                  <td>{row.title}<div className="hint">{text(row.description)}</div></td>
                  <td>{row.requiredDirects}</td>
                  <td><Badge value={row.status} /></td>
                  <td>{when(row.createdAt)}</td>
                </tr>
              )) : <EmptyRow cols={5} text="No rewards" />}
            </tbody>
          </table>
        </div>
      )}
      {editor ? (
        <RewardForm
          key={editor.id || "new"}
          initial={editor.id ? editor : null}
          onClose={() => setEditor(null)}
          onSaved={(message) => {
            setEditor(null);
            setNotice(message);
            list.reload();
          }}
        />
      ) : null}
    </Page>
  );
}

function RewardForm({ initial, onClose, onSaved }) {
  const [form, setForm] = useState(formFrom(initial));
  const [image, setImage] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [errors, setErrors] = useState({});

  function set(key, value) {
    setForm((current) => ({ ...current, [key]: value }));
  }

  async function onSubmit(event) {
    event.preventDefault();
    const imageError = checkImage(image);
    if (imageError) {
      setErrors({ image: imageError });
      return;
    }
    setBusy(true);
    setError("");
    setErrors({});
    const body = new FormData();
    body.append("title", form.title.trim());
    body.append("description", form.description.trim());
    body.append("requiredDirects", form.requiredDirects);
    body.append("status", form.status);
    if (image) body.append("image", image);
    try {
      if (initial) await api("/rewards/" + initial.id, { method: "PATCH", body });
      else await api("/rewards", { method: "POST", body });
      onSaved(initial ? "Reward saved" : "Reward created");
    } catch (err) {
      setError(errorText(err));
      setErrors(err.errors || {});
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal title={initial ? "Edit reward" : "New reward"} onClose={onClose}>
      <form onSubmit={onSubmit}>
        <Alerts error={error} />
        <div className="stack">
          <Field label="Title" error={errors.title}>
            <input maxLength={80} value={form.title} onChange={(event) => set("title", event.target.value)} />
          </Field>
          <Field label="Description" error={errors.description}>
            <textarea maxLength={2000} value={form.description} onChange={(event) => set("description", event.target.value)} />
          </Field>
          <Field label="Direct members required" error={errors.requiredDirects}>
            <input type="number" min="1" max="10000" step="1" value={form.requiredDirects} onChange={(event) => set("requiredDirects", event.target.value)} />
          </Field>
          <Field label="Status" error={errors.status}>
            <select value={form.status} onChange={(event) => set("status", event.target.value)}>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </Field>
          <Field label="Image" error={errors.image} hint="Optional. Max 2MB.">
            <input type="file" accept="image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp" onChange={(event) => setImage(event.target.files[0] || null)} />
          </Field>
          {image ? <p className="hint">{image.name}</p> : null}
          {initial?.image && !image ? <Photo src={initial.image} alt="" /> : null}
          {initial ? <p className="hint">Updated {when(initial.updatedAt)}</p> : null}
        </div>
        <div className="form-actions">
          <button type="button" className="btn ghost" onClick={onClose}>Cancel</button>
          <button className="btn" disabled={busy}>{busy ? "Saving..." : "Save reward"}</button>
        </div>
      </form>
    </Modal>
  );
}
